import { useState, useEffect, useRef, useCallback } from 'react';
import { ConsoleLogMessage, ServerState, WS_EVENTS, PlayerInfo } from '@kcdash/shared';
import { api } from './api.js';

interface UseServerWebSocketOptions {
  serverId?: string;
  onLog?: (log: ConsoleLogMessage) => void;
  onStatusChange?: (state: ServerState) => void;
  onPlayersChange?: (players: PlayerInfo[]) => void;
}

export function useServerWebSocket(options: UseServerWebSocketOptions) {
  const { serverId, onLog, onStatusChange, onPlayersChange } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [logs, setLogs] = useState<ConsoleLogMessage[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  // Stable callbacks via ref to avoid reconnect loops
  const onLogRef = useRef(onLog);
  onLogRef.current = onLog;

  const onStatusRef = useRef(onStatusChange);
  onStatusRef.current = onStatusChange;

  const onPlayersRef = useRef(onPlayersChange);
  onPlayersRef.current = onPlayersChange;

  useEffect(() => {
    if (!serverId) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const token = api.getToken() || '';
    const wsUrl = `${protocol}//${host}/ws?serverId=${encodeURIComponent(serverId)}&token=${encodeURIComponent(token)}`;

    let ws: WebSocket;
    let reconnectTimeout: any = null;
    let isDisposed = false;

    function connect() {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (isDisposed) return;
          setIsConnected(true);
        };

        ws.onmessage = (event) => {
          if (isDisposed) return;
          try {
            const data = JSON.parse(event.data);

            if (data.event === 'console:history' && Array.isArray(data.logs)) {
              setLogs(data.logs.slice(-1000));
            }

            if (data.event === WS_EVENTS.CONSOLE_LOG && data.data) {
              setLogs((prev) => {
                const next = [...prev, data.data];
                return next.slice(-1000); // keep last 1000 in memory
              });
              onLogRef.current?.(data.data);
            }

            if (data.event === WS_EVENTS.SERVER_STATUS && data.state) {
              onStatusRef.current?.(data.state);
            }

            if (data.event === WS_EVENTS.PLAYER_UPDATE && data.players) {
              onPlayersRef.current?.(data.players);
            }
          } catch {
            // Non-JSON message
          }
        };

        ws.onclose = () => {
          if (isDisposed) return;
          setIsConnected(false);
          reconnectTimeout = setTimeout(connect, 3000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        reconnectTimeout = setTimeout(connect, 3000);
      }
    }

    connect();

    return () => {
      isDisposed = true;
      clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [serverId]);

  const sendCommand = useCallback(
    (command: string) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            action: 'command',
            command,
          })
        );
      } else if (serverId) {
        // Fallback to HTTP API
        api.sendCommand(serverId, command).catch(console.error);
      }
    },
    [serverId]
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  return { isConnected, logs, sendCommand, clearLogs, setLogs };
}
